import { Button } from "@chakra-ui/react";
import { Plus } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppIcon from "@/components/Core/AppIcon/AppIcon";
import KnowledgeBaseTrigger from "@/components/Shared/KnowledgeBase/KnowledgeBaseTrigger";
import FilterEditSheet from "./FilterEditSheet";
import SavedFiltersManager from "./SavedFiltersManager";
import { SavedFilter } from "./types";

function newId(): string {
	return `${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
}

interface Props<TDef> {
	title: string;
	description: string;
	/** KB article tag for the section's help content. */
	kbTag: string;
	emptyTitle: string;
	emptyDesc: string;
	/** The currently-persisted filters (source of truth; the section keeps its own draft). */
	filters: SavedFilter<TDef>[];
	/** Persist the cleaned draft (parent decides which org field it lands on). */
	onSave: (next: SavedFilter<TDef>[]) => void;
	isSaving?: boolean;
	/** Seed value for a brand-new filter's definition. */
	emptyDefinition: TDef;
	/** Renders the domain's filter controls inside the editor sheet. */
	renderBuilder: (definition: TDef, onChange: (def: TDef) => void) => React.ReactNode;
}

/**
 * Admin editor for one set of organisation-defined filters (e.g. consultant or
 * customer presets). Holds a local draft seeded from the persisted list; the
 * editor sheet commits to the draft and the Save button persists the whole set.
 * Generic over the definition type so it serves any domain.
 */
function OrgFiltersSection<TDef>({
	title,
	description,
	kbTag,
	emptyTitle,
	emptyDesc,
	filters: persisted,
	onSave,
	isSaving,
	emptyDefinition,
	renderBuilder,
}: Props<TDef>) {
	const { t } = useTranslation();

	const [filters, setFilters] = useState<SavedFilter<TDef>[]>(persisted);
	// The filter currently open in the editor sheet. `isNew` filters aren't in
	// `filters` yet — they're appended only when the edit is committed.
	const [editing, setEditing] = useState<{ id: string; isNew: boolean } | null>(null);

	// Re-seed the draft whenever the persisted list changes (e.g. after a save).
	useEffect(() => {
		setFilters(persisted);
	}, [persisted]);

	const editingFilter = editing ? filters.find((f) => f.id === editing.id) : undefined;

	const openAdd = () => setEditing({ id: newId(), isNew: true });
	const openEdit = (id: string) => setEditing({ id, isNew: false });
	const remove = (id: string) => setFilters((prev) => prev.filter((f) => f.id !== id));

	const commitEdit = (name: string, definition: TDef) => {
		if (!editing) return;
		setFilters((prev) =>
			editing.isNew
				? [...prev, { id: editing.id, name, definition }]
				: prev.map((f) => (f.id === editing.id ? { ...f, name, definition } : f))
		);
		setEditing(null);
	};

	const save = () => {
		const cleaned = filters
			.map((f) => ({ ...f, name: f.name.trim() }))
			.filter((f) => f.name.length > 0);
		onSave(cleaned);
	};

	const isDirty = JSON.stringify(filters) !== JSON.stringify(persisted);

	return (
		<div>
			<div className="flex items-center gap-1.5">
				<p className="text-base font-semibold text-gray-800">{title}</p>
				<KnowledgeBaseTrigger tag={kbTag} />
			</div>
			<p className="mb-4 mt-1 text-sm text-gray-500">{description}</p>

			{filters.length === 0 ? (
				<div className="rounded-md border border-dashed border-gray-300 bg-gray-50/60 px-6 py-10 text-center">
					<p className="text-sm font-medium text-gray-700">{emptyTitle}</p>
					<p className="mx-auto mt-1.5 max-w-md text-sm text-gray-500">{emptyDesc}</p>
					<KnowledgeBaseTrigger tag={kbTag} margin="0.75rem 0 0 0">
						<span className="inline-flex items-center gap-1.5 text-sm font-medium text-action hover:underline">
							<AppIcon icon="help" size="14px" margin="0" />
							{t("LEARN_MORE" as any)}
						</span>
					</KnowledgeBaseTrigger>
				</div>
			) : (
				<SavedFiltersManager
					savedFilters={filters}
					onSelect={openEdit}
					onDelete={remove}
					onReorder={setFilters}
				/>
			)}

			<div className="mt-3">
				<button
					type="button"
					onClick={openAdd}
					className="flex items-center gap-1.5 rounded-sm px-1 py-1 text-sm font-medium text-action transition-colors hover:bg-action/10"
				>
					<Plus size={16} />
					{t("ADD_FILTER" as any)}
				</button>
			</div>

			<div className="mt-5">
				<Button
					size="sm"
					colorScheme="green"
					isLoading={isSaving}
					isDisabled={!isDirty}
					onClick={save}
				>
					{t("SAVE" as any)}
				</Button>
			</div>

			<FilterEditSheet
				open={!!editing}
				onClose={() => setEditing(null)}
				isNew={!!editing?.isNew}
				initialName={editingFilter?.name ?? ""}
				initialDefinition={editingFilter?.definition ?? emptyDefinition}
				onSave={commitEdit}
				renderBuilder={renderBuilder}
			/>
		</div>
	);
}

export default OrgFiltersSection;
